import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { clientsApi, nutstoreApi } from '@/lib/api'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../../components/ui/table'
import { Button } from '@/components/ui/button'
import { Plus, Settings2, Loader2, Database, Cloud, AlertCircle, Check, CheckCircle2, XCircle, ChevronDown, ChevronUp, Trash2, PowerOff, RotateCcw } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useState, useMemo } from 'react'
import { toast } from 'sonner'
import { useAuthStore } from '@/store/auth'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { PageHeader } from '@/components/layout'

interface ClientConfig {
  folderName: string
  code: string
  billingType: 'source' | 'target'
  enableTM: boolean
}

function generateClientCode(name: string): string {
  const words = name.split(/[\s_-]+/)
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

export function ClientList() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set())
  const [clientConfigs, setClientConfigs] = useState<Map<string, ClientConfig>>(new Map())
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    billingType: 'source' as 'source' | 'target',
    enableTM: false,
  })
  // 显示已停用/已删除客户（恢复停用、彻底删除的入口）
  const [showInactive, setShowInactive] = useState(false)
  // 待确认的危险操作：deactivate=停用 | purge=彻底删除
  const [confirmAction, setConfirmAction] = useState<{
    type: 'deactivate' | 'purge'
    client: any
  } | null>(null)
  // 彻底删除被拒时的未归档项目清单（后端 409 返回）
  const [purgeBlockedProjects, setPurgeBlockedProjects] = useState<any[] | null>(null)

  const canSync = user?.role === 'founder' || user?.role === 'pm'

  const { data: response, isLoading } = useQuery({
    queryKey: ['clients', showInactive],
    queryFn: () => clientsApi.getList(showInactive || undefined)
  })

  const { data: foldersResponse, isLoading: foldersLoading, refetch: refetchFolders } = useQuery({
    queryKey: ['nutstore-folders'],
    queryFn: () => nutstoreApi.getFolders(),
    enabled: isSyncDialogOpen && canSync,
  })

  const folders = (foldersResponse as any)?.data || []
  
  const { syncedFolders, unsyncedFolders } = useMemo(() => {
    const synced = folders.filter((f: any) => f.isSynced)
    const unsynced = folders.filter((f: any) => !f.isSynced)
    return { syncedFolders: synced, unsyncedFolders: unsynced }
  }, [folders])

  const createClientMutation = useMutation({
    mutationFn: (data: any) => clientsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success('客户创建成功')
      setIsAddDialogOpen(false)
      setFormData({ code: '', name: '', billingType: 'source', enableTM: false })
    },
    onError: (error: any) => {
      toast.error(`创建失败: ${error.message || '未知错误'}`)
    },
    onSettled: () => {
      setIsSubmitting(false)
    }
  })

  const importClientsMutation = useMutation({
    mutationFn: (clients: Array<{
      folderName: string
      code?: string
      billingType?: 'source' | 'target'
      enableTM?: boolean
    }>) => nutstoreApi.importClients(clients),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      refetchFolders()
      setSelectedFolders(new Set())
      setClientConfigs(new Map())
      setExpandedFolders(new Set())
      const { newClients, skippedClients } = res.data || {}
      if (newClients?.length > 0) {
        toast.success(`成功导入 ${newClients.length} 个客户`)
      }
      if (skippedClients?.length > 0) {
        toast.info(`${skippedClients.length} 个客户已存在，已跳过`)
      }
    },
    onError: (error: any) => {
      toast.error(`导入失败: ${error.error || error.message || '未知错误'}`)
    },
  })

  const confirmClientMutation = useMutation({
    mutationFn: (clientId: string) => nutstoreApi.confirmClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success('已确认客户信息')
    },
    onError: (error: any) => {
      toast.error(`确认失败: ${error.error || '未知错误'}`)
    },
  })

  // 停用客户（软删除：列表隐藏、数据全保留、可恢复）
  const deactivateClientMutation = useMutation({
    mutationFn: (clientId: string) => clientsApi.delete(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success('客户已停用（数据已保留，可恢复）')
      setConfirmAction(null)
    },
    onError: (error: any) => {
      toast.error(`停用失败: ${error.error || '未知错误'}`)
    },
  })

  // 恢复停用客户
  const restoreClientMutation = useMutation({
    mutationFn: (clientId: string) => clientsApi.update(clientId, { isActive: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success('客户已恢复')
    },
    onError: (error: any) => {
      toast.error(`恢复失败: ${error.error || '未知错误'}`)
    },
  })

  // 彻底删除客户（清空 TM/术语/RAG/反馈知识资产，保留项目记录；要求全部项目已归档）
  const purgeClientMutation = useMutation({
    mutationFn: (clientId: string) => clientsApi.purge(clientId),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      const deletedFiles = res?.data?.deletedR2Files
      toast.success(`已彻底删除${deletedFiles ? `（清理 ${deletedFiles} 个反馈文件）` : ''}，项目记录已保留`)
      setConfirmAction(null)
      setPurgeBlockedProjects(null)
    },
    onError: (error: any) => {
      // 409：存在未归档项目，展示清单引导先归档
      if (error?.data?.length > 0) {
        setPurgeBlockedProjects(error.data)
        toast.error(error.error || '存在未归档项目，无法彻底删除')
      } else {
        toast.error(`彻底删除失败: ${error.error || '未知错误'}`)
      }
    },
  })

  const handleAddClient = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.code || !formData.name) {
      toast.error('请填写完整信息')
      return
    }
    setIsSubmitting(true)
    createClientMutation.mutate(formData)
  }

  const handleConfirmClient = (clientId: string) => {
    confirmClientMutation.mutate(clientId)
  }

  const toggleFolder = (folderName: string) => {
    const newSelected = new Set(selectedFolders)
    if (newSelected.has(folderName)) {
      newSelected.delete(folderName)
      const newConfigs = new Map(clientConfigs)
      newConfigs.delete(folderName)
      setClientConfigs(newConfigs)
      const newExpanded = new Set(expandedFolders)
      newExpanded.delete(folderName)
      setExpandedFolders(newExpanded)
    } else {
      newSelected.add(folderName)
      const newConfigs = new Map(clientConfigs)
      newConfigs.set(folderName, {
        folderName,
        code: generateClientCode(folderName),
        billingType: 'source',
        enableTM: false,
      })
      setClientConfigs(newConfigs)
    }
    setSelectedFolders(newSelected)
  }

  const toggleExpand = (folderName: string) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderName)) {
      newExpanded.delete(folderName)
    } else {
      newExpanded.add(folderName)
    }
    setExpandedFolders(newExpanded)
  }

  const updateClientConfig = (folderName: string, updates: Partial<ClientConfig>) => {
    const newConfigs = new Map(clientConfigs)
    const existing = newConfigs.get(folderName)
    if (existing) {
      newConfigs.set(folderName, { ...existing, ...updates })
      setClientConfigs(newConfigs)
    }
  }

  const selectAllUnsynced = () => {
    const allUnsynced = unsyncedFolders.map((f: any) => f.name)
    setSelectedFolders(new Set(allUnsynced))
    const newConfigs = new Map(clientConfigs)
    allUnsynced.forEach((name: string) => {
      newConfigs.set(name, {
        folderName: name,
        code: generateClientCode(name),
        billingType: 'source',
        enableTM: false,
      })
    })
    setClientConfigs(newConfigs)
  }

  const clearSelection = () => {
    setSelectedFolders(new Set())
    setClientConfigs(new Map())
    setExpandedFolders(new Set())
  }

  const handleImportSelected = () => {
    if (selectedFolders.size === 0) {
      toast.error('请选择要导入的客户')
      return
    }
    const clients = Array.from(selectedFolders).map(folderName => {
      const config = clientConfigs.get(folderName)
      return {
        folderName,
        code: config?.code || generateClientCode(folderName),
        billingType: config?.billingType || 'source',
        enableTM: config?.enableTM || false,
      }
    })
    importClientsMutation.mutate(clients)
  }

  const clients = (response as any)?.data || []

  if (isLoading) return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      加载中...
    </div>
  )

  return (
    <div className="space-y-4">
      <PageHeader
        title="客户知识管理"
        description="管理客户的 RAG 知识库、术语库和反馈"
        actions={
          <div className="flex gap-2">
          {canSync && (
            <Dialog open={isSyncDialogOpen} onOpenChange={(open) => {
              setIsSyncDialogOpen(open)
              if (!open) {
                setSelectedFolders(new Set())
                setClientConfigs(new Map())
                setExpandedFolders(new Set())
              }
            }}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Cloud className="mr-2 h-4 w-4" /> 从坚果云导入
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[85vh]">
                <DialogHeader>
                  <DialogTitle>从坚果云导入客户</DialogTitle>
                  <DialogDescription>
                    扫描坚果云"译社项目"文件夹，选择要导入的客户并配置信息
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  {foldersLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4">
                          <span className="text-muted-foreground">
                            共 <strong>{folders.length}</strong> 个文件夹
                          </span>
                          <span className="text-success">
                            已同步: <strong>{syncedFolders.length}</strong>
                          </span>
                          <span className="text-warning">
                            未同步: <strong>{unsyncedFolders.length}</strong>
                          </span>
                        </div>
                        {unsyncedFolders.length > 0 && (
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={selectAllUnsynced}
                              disabled={selectedFolders.size === unsyncedFolders.length}
                            >
                              全选未同步
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={clearSelection}
                              disabled={selectedFolders.size === 0}
                            >
                              清除选择
                            </Button>
                          </div>
                        )}
                      </div>

                      <ScrollArea className="h-[450px] border rounded-lg">
                        <div className="p-2">
                          {unsyncedFolders.length > 0 && (
                            <>
                              <div className="text-xs font-medium text-warning-foreground px-2 py-1 bg-warning/10 rounded mb-2">
                                未同步 ({unsyncedFolders.length}) - 点击选择，展开配置
                              </div>
                              {unsyncedFolders.map((folder: any) => {
                                const isSelected = selectedFolders.has(folder.name)
                                const isExpanded = expandedFolders.has(folder.name)
                                const config = clientConfigs.get(folder.name)
                                
                                return (
                                  <div key={folder.path} className="mb-1">
                                    <div 
                                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 transition-colors ${
                                        isSelected ? 'bg-primary/10' : ''
                                      }`}
                                      onClick={() => toggleFolder(folder.name)}
                                    >
                                      <Checkbox 
                                        checked={isSelected}
                                        onCheckedChange={() => toggleFolder(folder.name)}
                                      />
                                      <span className="text-sm flex-1 font-medium">{folder.name}</span>
                                      {isSelected && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            toggleExpand(folder.name)
                                          }}
                                        >
                                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                        </Button>
                                      )}
                                      <Badge variant="outline" className="text-warning border-warning/30 bg-warning/10">
                                        <XCircle className="h-3 w-3 mr-1" /> 未同步
                                      </Badge>
                                    </div>
                                    
                                    {isSelected && isExpanded && config && (
                                      <div className="ml-8 mr-2 mt-1 p-3 bg-muted/30 rounded-lg space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                          <div className="space-y-1">
                                            <Label className="text-xs">客户代码</Label>
                                            <Input 
                                              value={config.code}
                                              onChange={(e) => updateClientConfig(folder.name, { code: e.target.value.toUpperCase() })}
                                              className="h-8 text-sm"
                                              placeholder="如: LOBE"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs">计费方式</Label>
                                            <Select 
                                              value={config.billingType}
                                              onValueChange={(value: 'source' | 'target') => updateClientConfig(folder.name, { billingType: value })}
                                            >
                                              <SelectTrigger className="h-8 text-sm">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="source">按源文字数</SelectItem>
                                                <SelectItem value="target">按目标文字数</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <Database className="h-3.5 w-3.5 text-muted-foreground" />
                                            <Label className="text-xs cursor-pointer">启用翻译记忆库</Label>
                                          </div>
                                          <Switch
                                            checked={config.enableTM}
                                            onCheckedChange={(checked) => updateClientConfig(folder.name, { enableTM: checked })}
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </>
                          )}

                          {syncedFolders.length > 0 && (
                            <>
                              <div className="text-xs font-medium text-success-foreground px-2 py-1 bg-success/10 rounded mb-2 mt-4">
                                已同步 ({syncedFolders.length})
                              </div>
                              {syncedFolders.map((folder: any) => (
                                <div 
                                  key={folder.path} 
                                  className="flex items-center gap-3 p-2 rounded bg-muted/30"
                                >
                                  <Checkbox disabled />
                                  <span className="text-sm flex-1 text-muted-foreground">{folder.name}</span>
                                  <Badge variant="outline" className="text-success border-success/30 bg-success/10">
                                    <CheckCircle2 className="h-3 w-3 mr-1" /> 已同步
                                  </Badge>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </ScrollArea>

                      {selectedFolders.size > 0 && (
                        <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                          <span className="text-sm">
                            已选择 <strong>{selectedFolders.size}</strong> 个客户
                          </span>
                          <Button 
                            size="sm" 
                            onClick={handleImportSelected}
                            disabled={importClientsMutation.isPending}
                          >
                            {importClientsMutation.isPending && (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            导入选中客户
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setIsSyncDialogOpen(false)
                    setSelectedFolders(new Set())
                    setClientConfigs(new Map())
                    setExpandedFolders(new Set())
                  }}>
                    关闭
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" /> 新增客户
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleAddClient}>
                <DialogHeader>
                  <DialogTitle>新增客户</DialogTitle>
                  <DialogDescription>
                    创建一个新的客户档案，用于管理其专有的翻译资产。
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="code">客户代码</Label>
                    <Input 
                      id="code" 
                      placeholder="例如: LOBE, PANDA" 
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="name">客户名称</Label>
                    <Input 
                      id="name" 
                      placeholder="客户全称" 
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="billingType">计费方式</Label>
                    <Select 
                      value={formData.billingType} 
                      onValueChange={(value: any) => setFormData({ ...formData, billingType: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="选择计费方式" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="source">按源文字数</SelectItem>
                        <SelectItem value="target">按目标文字数</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Database className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor="tm-toggle" className="cursor-pointer">启用翻译记忆库</Label>
                    </div>
                    <Switch
                      id="tm-toggle"
                      checked={formData.enableTM}
                      onCheckedChange={(checked) => setFormData({ ...formData, enableTM: checked })}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground -mt-2">
                    启用后，该客户已完成项目的译文将自动存入记忆库，后续项目可复用
                  </p>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    确认创建
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        }
      />

      <div className="rounded-lg border bg-card">
        {canSync && (
          <div className="flex items-center justify-end gap-2 px-4 py-2 border-b">
            <label htmlFor="show-inactive" className="text-xs text-muted-foreground cursor-pointer">
              显示已停用/已删除客户
            </label>
            <Checkbox
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>客户代码</TableHead>
              <TableHead>客户名称</TableHead>
              <TableHead>计费方式</TableHead>
              <TableHead>翻译记忆库</TableHead>
              <TableHead>状态</TableHead>
              <TableHead className="text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((client: any) => {
              // purge 后 code 被改写为 DEL_xxx 释放唯一约束，据此区分「停用」与「已删除」
              const isPurged = !client.isActive && String(client.code).startsWith('DEL_')
              return (
              <TableRow key={client.id} className={isPurged ? 'opacity-50' : ''}>
                <TableCell className="font-medium">{client.code}</TableCell>
                <TableCell className="flex items-center gap-2">
                  {client.name}
                  {client.needsUpdate && !isPurged && (
                    <Badge variant="secondary" className="bg-warning/10 text-warning-foreground text-[10px]">
                      <AlertCircle className="h-3 w-3 mr-1" /> 待完善
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{client.billingType === 'source' ? '按源文字数' : '按目标文字数'}</TableCell>
                <TableCell>
                  {client.enableTM ? (
                    <Badge variant="secondary" className="bg-info/10 text-black text-[10px]">
                      <Database className="h-3 w-3 mr-1" /> 已启用
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-xs">未启用</span>
                  )}
                </TableCell>
                <TableCell>
                  {isPurged ? (
                    <Badge variant="secondary" className="text-[10px]">已删除</Badge>
                  ) : (
                    <Badge variant={client.isActive ? 'default' : 'secondary'} className="text-[10px]">
                      {client.isActive ? '活跃' : '停用'}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {isPurged ? (
                      <span className="text-xs text-muted-foreground">知识资产已清空</span>
                    ) : (
                      <>
                        {client.isActive ? (
                          <>
                            {client.needsUpdate && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleConfirmClient(client.id)}
                                disabled={confirmClientMutation.isPending}
                                title="确认客户信息已完善"
                              >
                                <Check className="h-3 w-3 text-success" />
                              </Button>
                            )}
                            <Button variant="outline" size="sm" asChild>
                              <Link to="/clients/$clientId" params={{ clientId: client.id }}>
                                <Settings2 className="mr-2 h-3 w-3" /> 管理
                              </Link>
                            </Button>
                            {canSync && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmAction({ type: 'deactivate', client })}
                                title="停用客户（软删除，可恢复）"
                              >
                                <PowerOff className="h-3 w-3" />
                              </Button>
                            )}
                          </>
                        ) : (
                          canSync && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => restoreClientMutation.mutate(client.id)}
                              disabled={restoreClientMutation.isPending}
                              title="恢复客户"
                            >
                              <RotateCcw className="h-3 w-3 text-success" /> 恢复
                            </Button>
                          )
                        )}
                        {canSync && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPurgeBlockedProjects(null)
                              setConfirmAction({ type: 'purge', client })
                            }}
                            title="彻底删除客户（清空知识资产，保留项目记录）"
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              )
            })}
            {clients.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <Database className="h-8 w-8 opacity-40" />
                    <p>暂无客户数据</p>
                    <p className="text-xs">点击右上角「新增客户」或从坚果云导入</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* 停用/彻底删除确认对话框：purge 展示删除与保留清单，409 时展示未归档项目 */}
      <Dialog
        open={!!confirmAction}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmAction(null)
            setPurgeBlockedProjects(null)
          }
        }}
      >
        <DialogContent>
          {confirmAction?.type === 'deactivate' ? (
            <>
              <DialogHeader>
                <DialogTitle>停用客户「{confirmAction.client.name}」</DialogTitle>
                <DialogDescription>
                  停用后该客户从列表隐藏，全部数据（TM、术语、RAG、反馈、项目）保留，可随时恢复。适合暂停合作的场景。
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmAction(null)}>取消</Button>
                <Button
                  variant="secondary"
                  disabled={deactivateClientMutation.isPending}
                  onClick={() => deactivateClientMutation.mutate(confirmAction.client.id)}
                >
                  {deactivateClientMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  确认停用
                </Button>
              </DialogFooter>
            </>
          ) : confirmAction?.type === 'purge' ? (
            <>
              <DialogHeader>
                <DialogTitle>彻底删除客户「{confirmAction.client.name}」</DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-3 text-sm">
                    <p>该操作不可恢复，请确认：</p>
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-1">
                      <p className="font-medium text-destructive">将删除（知识资产）：</p>
                      <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                        <li>翻译记忆库（TM）、术语表、术语候选</li>
                        <li>RAG 知识库、RAG 确认记录</li>
                        <li>客户反馈记录及反馈附件文件</li>
                      </ul>
                    </div>
                    <div className="rounded-md border bg-muted/40 p-3 space-y-1">
                      <p className="font-medium">将保留（经营凭证）：</p>
                      <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                        <li>项目记录（字数计费依据、状态、时间线）</li>
                        <li>审计日志与统计报表</li>
                      </ul>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      前提：该客户所有项目均已归档，否则删除会被拒绝。
                    </p>
                    {purgeBlockedProjects && purgeBlockedProjects.length > 0 && (
                      <div className="rounded-md border border-warning bg-warning/10 p-3 space-y-1">
                        <p className="font-medium text-warning-foreground">
                          存在 {purgeBlockedProjects.length} 个未归档项目，请先在项目页归档：
                        </p>
                        <ul className="list-disc list-inside text-xs text-muted-foreground max-h-32 overflow-auto">
                          {purgeBlockedProjects.map((p: any) => (
                            <li key={p.id}>{p.fileName}（{p.status}）</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmAction(null)}>取消</Button>
                <Button
                  variant="destructive"
                  disabled={purgeClientMutation.isPending}
                  onClick={() => purgeClientMutation.mutate(confirmAction.client.id)}
                >
                  {purgeClientMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  彻底删除
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}
